import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  type CollisionDetection,
  type DragEndEvent,
  type DragStartEvent
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { useTranslation } from 'react-i18next';
import { IconClose, IconPlus } from '../UI';
import { Button } from '../UI/base/Button';
import { DropdownList } from '../UI/base/DropdownList';
import type {
  InterventionGroupTemplateResponse,
  InterventionTemplateResponse
} from '../../api/generated';
import { PlacementRuleType } from '../../api/generated';
import { AllowedDaysModal } from './interventionGraph/AllowedDaysModal';
import { BranchConnectors } from './interventionGraph/BranchConnectors';
import { Connector } from './interventionGraph/Connector';
import { computeBranchConnections } from './interventionGraph/connectors';
import { computeChainIndents } from './interventionGraph/positioning';
import {
  gapDropId,
  isGapDropAllowed,
  resolveValidDragEnd
} from './interventionGraph/dropResolution';
import { useBranchConnectorPaths } from './interventionGraph/useBranchConnectorPaths';
import { FreeNode } from './interventionGraph/FreeNode';
import { FreePool } from './interventionGraph/FreePool';
import { GapDropZone } from './interventionGraph/GapDropZone';
import { InterventionNode } from './interventionGraph/InterventionNode';
import { computeLayout, isAnchoredChain, type Chain } from './interventionGraph/layout';
import { computeInstanceLabels } from './interventionGraph/instanceLabels';
import { removeNode } from './interventionGraph/mutations';
import {
  editorToGroup,
  groupToEditor,
  makeEditorNodeFromCatalog
} from './interventionGraph/adapters';
import type { EditorAllowedDay, EditorNode } from './interventionGraph/types';
import styles from './InterventionGraphEditor.module.css';

interface InterventionGraphEditorProps {
  group: InterventionGroupTemplateResponse;
  catalog: InterventionTemplateResponse[];
  strokeColor: string;
  fillColor: string;
  onChange: (group: InterventionGroupTemplateResponse) => void;
}

export const InterventionGraphEditor = ({
  group,
  catalog,
  strokeColor,
  fillColor,
  onChange
}: InterventionGraphEditorProps) => {
  const { t } = useTranslation();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const addRowRef = useRef<HTMLDivElement>(null);
  const [editingDaysWiId, setEditingDaysWiId] = useState<string | null>(null);
  const [daysPopoverRect, setDaysPopoverRect] = useState<DOMRect | null>(null);

  const chainsContentRef = useRef<HTMLDivElement>(null);
  const graphRef = useRef<HTMLDivElement>(null);

  // The add-intervention panel may grow up to the group's full width before its
  // options wrap; cap it at that width, measured when the panel opens.
  const [panelMaxWidth, setPanelMaxWidth] = useState<number>();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const editor = useMemo(() => groupToEditor(group), [group]);

  const commit = useCallback(
    (nextEditor: EditorNode[]) => onChange(editorToGroup(group, nextEditor)),
    [group, onChange]
  );

  const { chains, free } = useMemo(() => computeLayout(editor), [editor]);

  const anchoredChains = useMemo(() => chains.filter(isAnchoredChain), [chains]);
  const floatingChains = useMemo(() => chains.filter((c) => !isAnchoredChain(c)), [chains]);
  // Chains in render (top-to-bottom) order: anchored rows first, then the rest.
  const orderedChains = useMemo(
    () => [...anchoredChains, ...floatingChains],
    [anchoredChains, floatingChains]
  );

  // Horizontal indent per row, so a branch row lines up under the column after
  // the node it branches from (keeps the link line from crossing leftward).
  const chainIndents = useMemo(
    () => computeChainIndents(editor, orderedChains),
    [editor, orderedChains]
  );

  // Diagonal links for every cross-row dependency. Hidden mid-drag so the lines
  // don't lag behind the moving node.
  const branchConnections = useMemo(
    () => (activeId ? [] : computeBranchConnections(editor, orderedChains)),
    [activeId, editor, orderedChains]
  );
  const branchPaths = useBranchConnectorPaths(chainsContentRef, branchConnections);

  const isDraggingFree = useMemo(
    () => activeId !== null && free.some((wi) => wi.id === activeId),
    [activeId, free]
  );

  // Open the intervention list as soon as the search field appears, capping the
  // panel at the graph width at that moment.
  useEffect(() => {
    if (!showAdd) return;
    setPanelMaxWidth(graphRef.current?.clientWidth);
    addRowRef.current?.querySelector('input')?.focus();
  }, [showAdd]);

  const getWiName = useCallback(
    (wiId: string) => {
      const wi = editor.find((w) => w.id === wiId);
      return wi?.intervention.name ?? t('config-unknown-intervention');
    },
    [editor, t]
  );

  // Visual instance labels (number for arranged, letter for Flex), derived live
  // from the current layout. Never persisted; see instanceLabels.ts / ADR 0001.
  const instanceLabels = useMemo(
    () => computeInstanceLabels(orderedChains, free),
    [orderedChains, free]
  );

  const getWiLabel = useCallback(
    (wiId: string) => instanceLabels.get(wiId) ?? '',
    [instanceLabels]
  );

  const activeWi = useMemo(
    () => (activeId ? editor.find((wi) => wi.id === activeId) : null),
    [activeId, editor]
  );
  const activeInFree = useMemo(
    () => (activeId ? free.some((wi) => wi.id === activeId) : false),
    [activeId, free]
  );
  // The drag overlay mirrors the dragged card: dashed free-pool style when the
  // drag started in the Flex pool, solid chain style otherwise.
  const OverlayNode = activeInFree ? FreeNode : InterventionNode;

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleDragStart = ({ active }: DragStartEvent) => setActiveId(active.id as string);

  // Offer only drop targets whose drop would actually commit. A node or gap
  // that fails the drag-end guards (anchor violation, cycle) is filtered out
  // of the collisions, so dnd-kit never previews — shifts nodes toward — a
  // placement that would be rejected on drop, e.g. after a LAST-anchored block.
  const collisionDetection = useCallback<CollisionDetection>(
    (dragArgs) => {
      const activeWiId = String(dragArgs.active.id);
      return closestCenter(dragArgs).filter(
        ({ id }) => resolveValidDragEnd(editor, chains, free, activeWiId, String(id)) !== null
      );
    },
    [editor, chains, free]
  );

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent) => {
      setActiveId(null);
      if (!over || active.id === over.id) return;

      // resolveValidDragEnd is null for a drop that must be ignored: an
      // unresolvable target, a contradictory loop, or a block placed after a
      // LAST / before a FIRST anchor.
      const next = resolveValidDragEnd(editor, chains, free, String(active.id), String(over.id));
      if (next) commit(next);
    },
    [chains, free, editor, commit]
  );

  const handleRemove = useCallback(
    (wiId: string) => commit(removeNode(editor, wiId)),
    [editor, commit]
  );

  const handleRemoveDep = useCallback(
    (wiId: string, depId: string) =>
      commit(
        editor.map((wi) =>
          wi.id !== wiId
            ? wi
            : { ...wi, dependencies: wi.dependencies.filter((d) => d.id !== depId) }
        )
      ),
    [editor, commit]
  );

  const handleAddDep = useCallback(
    (wiId: string, type: PlacementRuleType, targetId?: string) =>
      commit(
        editor.map((wi) =>
          wi.id !== wiId
            ? wi
            : {
                ...wi,
                dependencies: [...wi.dependencies, { id: crypto.randomUUID(), type, targetId }]
              }
        )
      ),
    [editor, commit]
  );

  const handleUpdateAllowedDays = useCallback(
    (wiId: string, allowedDays: EditorAllowedDay[] | undefined) =>
      commit(editor.map((wi) => (wi.id !== wiId ? wi : { ...wi, allowedDays }))),
    [editor, commit]
  );

  const handleAddIntervention = useCallback(
    (found: InterventionTemplateResponse) => {
      commit([...editor, makeEditorNodeFromCatalog(found)]);
      setShowAdd(false);
    },
    [editor, commit]
  );

  // ── Render ─────────────────────────────────────────────────────────────────

  const renderChainRow = (chain: Chain) => {
    const indent = chainIndents.get(chain.id) ?? 0;
    return (
    <div
      key={chain.id}
      className={styles.chainRow}
      style={indent ? { paddingLeft: indent } : undefined}
    >
      <SortableContext
        items={chain.items.map((wi) => wi.id)}
        strategy={horizontalListSortingStrategy}
      >
        {isDraggingFree && (
          <GapDropZone
            id={gapDropId(chain.id, 0)}
            active={isGapDropAllowed(chain, 0)}
            strokeColor={strokeColor}
          />
        )}

        {chain.items.map((wi, idx) => {
          const primaryAfterType = wi.dependencies.find(
            (d) =>
              d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter
          )?.type;

          return (
            <Fragment key={wi.id}>
              <div className={styles.chainCell}>
                {idx > 0 && !isDraggingFree && (
                  <Connector
                    depType={primaryAfterType}
                    fromId={chain.items[idx - 1]?.id}
                    toId={wi.id}
                  />
                )}
                <div className={styles.nodeAnchor} data-node-id={wi.id}>
                  <InterventionNode
                    wi={wi}
                    strokeColor={strokeColor}
                    fillColor={fillColor}
                    allWeekInterventions={editor}
                    getWiName={getWiName}
                    getWiLabel={getWiLabel}
                    onRemove={() => handleRemove(wi.id)}
                    onRemoveDep={(depId) => handleRemoveDep(wi.id, depId)}
                    onAddDep={(type, targetId) => handleAddDep(wi.id, type, targetId)}
                    onEditAllowedDays={(rect) => {
                      setEditingDaysWiId(wi.id);
                      setDaysPopoverRect(rect);
                    }}
                  />
                </div>
              </div>
              {isDraggingFree && (
                <GapDropZone
                  id={gapDropId(chain.id, idx + 1)}
                  active={isGapDropAllowed(chain, idx + 1)}
                  strokeColor={strokeColor}
                />
              )}
            </Fragment>
          );
        })}
      </SortableContext>
    </div>
    );
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <div ref={graphRef} className={styles.graph}>
        {chains.length > 0 && (
          <div className={styles.chainsArea}>
            <div className={styles.chainsContent} ref={chainsContentRef}>
              <BranchConnectors paths={branchPaths} />
              {anchoredChains.map((c) => renderChainRow(c))}
              {floatingChains.map((c) => renderChainRow(c))}
            </div>
          </div>
        )}

        <FreePool
          free={free}
          allNodes={editor}
          strokeColor={strokeColor}
          fillColor={fillColor}
          getWiName={getWiName}
          getWiLabel={getWiLabel}
          onRemove={handleRemove}
          onRemoveDep={handleRemoveDep}
          onAddDep={handleAddDep}
          onEditAllowedDays={(wiId, rect) => {
            setEditingDaysWiId(wiId);
            setDaysPopoverRect(rect);
          }}
        />

        <div className={styles.addSlot}>
          <div
            ref={addRowRef}
            className={`${styles.addInputRow} ${showAdd ? '' : styles.addStateHidden}`}
            aria-hidden={!showAdd}
          >
            <DropdownList<InterventionTemplateResponse>
              options={catalog}
              getKey={(iv) => iv.id}
              renderLabel={(iv) => iv.name}
              getSearchText={(iv) => iv.name}
              selectionMode="single"
              value={[]}
              onChange={(_next, meta) => handleAddIntervention(meta.option)}
              searchable
              placeholder={t('config-add-intervention-placeholder')}
              noResultsText={t('config-add-intervention-no-results')}
              classes={styles.interventionPicker}
              panelMaxWidth={panelMaxWidth}
            />
            <button
              className={styles.addCancelBtn}
              onClick={() => setShowAdd(false)}
              title={t('config-cancel')}
              aria-label={t('config-cancel')}
            >
              <IconClose width={14} height={14} />
            </button>
          </div>
          <div className={showAdd ? styles.addStateHidden : undefined}>
            <Button
              type="tertiary"
              icon={<IconPlus width={16} height={16} />}
              iconPosition="start"
              onClick={() => setShowAdd(true)}
            >
              {t('config-add-intervention')}
            </Button>
          </div>
        </div>
      </div>

      <DragOverlay>
        {activeWi && (
          <OverlayNode
            wi={activeWi}
            strokeColor={strokeColor}
            fillColor={fillColor}
            isOverlay
            allWeekInterventions={[]}
            getWiName={getWiName}
            getWiLabel={getWiLabel}
            onRemove={() => {}}
            onRemoveDep={() => {}}
            onAddDep={() => {}}
            onEditAllowedDays={() => {}}
          />
        )}
      </DragOverlay>

      {editingDaysWiId &&
        (() => {
          const wi = editor.find((w) => w.id === editingDaysWiId);
          if (!wi) return null;
          return (
            <AllowedDaysModal
              wi={wi}
              interventionName={wi.intervention.name}
              strokeColor={strokeColor}
              triggerRect={daysPopoverRect}
              onSave={(allowedDays) => handleUpdateAllowedDays(editingDaysWiId, allowedDays)}
              onClose={() => {
                setEditingDaysWiId(null);
                setDaysPopoverRect(null);
              }}
            />
          );
        })()}
    </DndContext>
  );
};
